/**
 * NotificationToast — 알림형(Notification) 토스트 1건 (Shell 전속)
 *
 * 소유: 프레임 팀. 룩앤필은 임시(프리셋 토큰 기반) — UI팀 D-09 §15.8 확정 시 컴포넌트 교체.
 * 데이터 계약: 라벨(ZEU_NOTI_TYPE 코드명, F-03 §7.6) · 타이틀(title) · 본문(body) · 딥링크(targetType/targetId)
 * (아바타·썸네일 없음 — 데이터 미존재, UI팀_전달_알림토스트_정의.md)
 *
 * 동작: 타이머 바 자동 소멸. hover 시 타이머 일시정지. [나중에]=닫기 / [확인하기]=딥링크 이동+읽음.
 */
import { useEffect, useRef, useState } from 'react';
import { Button } from '@indipia/ui-core';
import { useAppStore } from '@indipia/shared';
import type { NotificationItem } from './types';
import { resolveSourceLabel } from './sourceLabel';

interface NotificationToastProps {
  item: NotificationItem;
  onDismiss: () => void;
  onConfirm: (item: NotificationItem) => void;
  durationMs?: number;
}

export function NotificationToast({
  item,
  onDismiss,
  onConfirm,
  durationMs = 6000,
}: NotificationToastProps) {
  const codes = useAppStore((s) => s.codes);
  const [remaining, setRemaining] = useState(100);
  const paused = useRef(false);
  const startedAt = useRef(Date.now());
  const elapsedBefore = useRef(0);

  useEffect(() => {
    const tick = setInterval(() => {
      if (paused.current) return;
      const elapsed = elapsedBefore.current + (Date.now() - startedAt.current);
      const pct = Math.max(0, 100 - (elapsed / durationMs) * 100);
      setRemaining(pct);
      if (pct <= 0) onDismiss();
    }, 50);
    return () => clearInterval(tick);
  }, [durationMs, onDismiss]);

  const onEnter = () => {
    paused.current = true;
    elapsedBefore.current += Date.now() - startedAt.current;
  };
  const onLeave = () => {
    paused.current = false;
    startedAt.current = Date.now();
  };

  return (
    <div
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      className="w-80 overflow-hidden rounded-lg border border-border bg-surface-raised shadow-md"
      role="status"
    >
      {/* 타이머 바 */}
      <div className="h-[3px] w-full bg-border">
        <div className="h-full bg-point" style={{ width: `${remaining}%` }} />
      </div>

      <div className="px-4 pb-3 pt-3">
        <div className="mb-0.5 text-xs font-medium text-point">
          {resolveSourceLabel(item, codes)}
        </div>
        <div className="mb-1 text-sm font-medium text-text">{item.title}</div>
        <p
          className="text-[13px] leading-snug text-text-muted"
          style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
        >
          {item.body}
        </p>
      </div>

      <div className="flex border-t border-border">
        <Button
          type="button"
          variant="ghost"
          onClick={onDismiss}
          className="flex-1 rounded-none border-r border-border"
        >
          나중에
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={() => onConfirm(item)}
          className="flex-1 rounded-none"
        >
          확인하기
        </Button>
      </div>
    </div>
  );
}
